import { supabase } from '../lib/supabase';

export interface SeoAuditRequest {
  id?: string;
  email: string;
  site_url: string;
  prospect_id?: string;
  status?: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface SeoAuditRequestInput {
  email: string;
  site_url: string;
  prospect_id?: string;
}

/**
 * Service pour gérer les demandes d'audit SEO
 */
export class SeoAuditRequestService {
  /**
   * Créer une nouvelle demande d'audit SEO
   */
  static async createRequest(data: SeoAuditRequestInput): Promise<SeoAuditRequest> {
    try {
      const { data: request, error } = await supabase
        .from('seo_audit_requests')
        .insert([{
          email: data.email,
          site_url: data.site_url,
          prospect_id: data.prospect_id || null,
          status: 'pending'
        }])
        .select()
        .single();

      if (error) {
        console.error('❌ [SeoAuditRequestService] Erreur création demande:', error);
        throw new Error(`Erreur lors de la création de la demande: ${error.message}`);
      }

      console.log('✅ [SeoAuditRequestService] Demande créée:', request.id);
      return request;
    } catch (error) {
      console.error('❌ [SeoAuditRequestService] Erreur inattendue:', error);
      throw error;
    }
  }

  /**
   * Récupérer toutes les demandes d'audit SEO (pour l'admin)
   */
  static async getAllRequests(options?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ requests: SeoAuditRequest[]; total: number }> {
    try {
      let query = supabase
        .from('seo_audit_requests')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false });

      if (options?.status) {
        query = query.eq('status', options.status);
      }

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      if (options?.offset) {
        query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
      }

      const { data: requests, error, count } = await query;

      if (error) {
        console.error('❌ [SeoAuditRequestService] Erreur récupération demandes:', error);
        throw new Error(`Erreur lors de la récupération des demandes: ${error.message}`);
      }

      console.log(`✅ [SeoAuditRequestService] ${requests?.length || 0} demandes récupérées`);
      return { requests: requests || [], total: count || 0 };
    } catch (error) {
      console.error('❌ [SeoAuditRequestService] Erreur inattendue:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour le statut d'une demande
   */
  static async updateRequestStatus(
    id: string,
    status: 'pending' | 'in_progress' | 'completed' | 'cancelled',
    notes?: string
  ): Promise<SeoAuditRequest> {
    try {
      const updateData: any = { status };
      if (notes !== undefined) {
        updateData.notes = notes;
      }

      const { data: request, error } = await supabase
        .from('seo_audit_requests')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('❌ [SeoAuditRequestService] Erreur mise à jour:', error);
        throw new Error(`Erreur lors de la mise à jour: ${error.message}`);
      }

      console.log('✅ [SeoAuditRequestService] Demande mise à jour:', id);
      return request;
    } catch (error) {
      console.error('❌ [SeoAuditRequestService] Erreur inattendue:', error);
      throw error;
    }
  }

  /**
   * Supprimer une demande
   */
  static async deleteRequest(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('seo_audit_requests')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('❌ [SeoAuditRequestService] Erreur suppression:', error);
        throw new Error(`Erreur lors de la suppression: ${error.message}`);
      }

      console.log('✅ [SeoAuditRequestService] Demande supprimée:', id);
    } catch (error) {
      console.error('❌ [SeoAuditRequestService] Erreur inattendue:', error);
      throw error;
    }
  }

  /**
   * Récupérer une demande spécifique
   */
  static async getRequestById(id: string): Promise<SeoAuditRequest | null> {
    try {
      const { data: request, error } = await supabase
        .from('seo_audit_requests')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // Pas trouvé
        }
        console.error('❌ [SeoAuditRequestService] Erreur récupération:', error);
        throw new Error(`Erreur lors de la récupération: ${error.message}`);
      }

      return request;
    } catch (error) {
      console.error('❌ [SeoAuditRequestService] Erreur inattendue:', error);
      throw error;
    }
  }
}

export default SeoAuditRequestService;
